"use client";

import { trackTemplateUsed } from "@/lib/analytics";
import { getRequiredTemplateConnectors } from "@/lib/templates/requiredConnectors";
import { showToast } from "@/lib/toast";
import { extractDBQueryTables } from "@/lib/workflow/dbQueryTables";
import { FROM_ONBOARDING_QUERY_PARAM } from "@/lib/workflow/firstPublish";
import { NOTIFICATION_CHANNELS, type NotificationConfig } from "@/lib/workflow/notificationChannels";
import { hasNotificationNodes, resolveNotificationNodes } from "@/lib/workflow/resolveNotification";
import { resolvePropsInDefinition } from "@/lib/workflow/resolveProps";
import type { Template, TemplateProp } from "@/stores/templatesStore";
import { useTemplatesStore } from "@/stores/templatesStore";
import type { WorkflowDefinition, WorkflowNode } from "@/stores/workflowsStore";
import { useWorkflowsStore } from "@/stores/workflowsStore";
import { useRouter } from "next/navigation";
import { useCallback, useMemo, useRef, useState } from "react";

/**
 * Shared hook for the "Use this template" flow used on the template detail page
 * and in the AI chat intercept card.
 *
 * Returns stable handlers plus the state needed to render ConnectorInputModal.
 *
 * `fromOnboarding`: when true, the editor URL the hook navigates to includes
 * `?fromOnboarding=1` so the workflow editor knows to surface the guided
 * 4-step Publish stepper. Callers that originate from the onboarding wizard
 * (or that detect the param on their own page URL) should opt in; everyone
 * else leaves it false so manual / template-detail visits skip the funnel.
 */
export function useTemplateAction({
  onSuccess,
  fromOnboarding = false
}: {
  onSuccess?: () => void;
  fromOnboarding?: boolean;
} = {}) {
  const templateToWorkflowRequest = useTemplatesStore(state => state.templateToWorkflowRequest);
  const createWorkflow = useWorkflowsStore(state_0 => state_0.createWorkflow);
  const router = useRouter();
  const [showConnectorModal, setShowConnectorModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [isCreatingWorkflow, setIsCreatingWorkflow] = useState(false);
  const getDBTables = useCallback((template: Template) => {
    const propDefaults = buildPropDefaults(template.templateProps);
    return extractDBQueryTables(template.definition as Parameters<typeof extractDBQueryTables>[0], propDefaults);
  }, []);
  const requiredTables = useMemo(() => selectedTemplate ? getDBTables(selectedTemplate) : [], [selectedTemplate, getDBTables]);

  // Keep onSuccess stable so it can be used in callbacks without triggering re-renders
  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;
  const applyConnectorMapping = useCallback((definition: WorkflowDefinition, connectorMapping: Record<string, string>): WorkflowDefinition => {
    if (!definition?.nodes) return definition;
    const updatedNodes: Record<string, WorkflowNode> = {};
    for (const [nodeId, node] of Object.entries(definition.nodes)) {
      const updatedNode = {
        ...node
      };
      if (node.connector?.type) {
        const connectorId = connectorMapping[nodeId] || connectorMapping[node.connector.type];
        if (connectorId) {
          updatedNode.connector = {
            type: node.connector.type === "turnkey" ? "wallet" : node.connector.type,
            id: connectorId
          };
        }
      }
      updatedNodes[nodeId] = updatedNode as WorkflowNode;
    }
    return {
      ...definition,
      nodes: updatedNodes
    };
  }, []);

  /**
   * Creates a workflow from the given template + connector/prop values.
   * Called either directly (no connectors needed) or from ConnectorInputModal's onSubmit.
   *
   * Prop values are inlined into the definition (replacing {{$props.X}} references)
   * so the workflow is self-contained and doesn't need runtime prop resolution.
   */
  const createWorkflowFromTemplate = useCallback(async (template_0: Template, connectorMapping_0: Record<string, string>, propValues?: Record<string, unknown>, payloadOverrides?: Record<string, Record<string, unknown>>) => {
    setIsCreatingWorkflow(true);
    try {
      trackTemplateUsed(template_0.id, template_0.name);
      const workflowRequest = templateToWorkflowRequest(template_0);
      let updatedDefinition = applyConnectorMapping(workflowRequest.definition, connectorMapping_0);

      // Inline prop values into the definition — replaces {{$props.X}} with actual values
      if (propValues && Object.keys(propValues).length > 0) {
        updatedDefinition = resolvePropsInDefinition(updatedDefinition, propValues);
      }

      // Inject direct payload overrides (e.g., eventAbi + filter for evm-log templates)
      if (payloadOverrides) {
        const updatedNodes_0 = {
          ...updatedDefinition.nodes
        };
        for (const [nodeId_0, overrides] of Object.entries(payloadOverrides)) {
          const node_0 = updatedNodes_0[nodeId_0];
          if (node_0) {
            updatedNodes_0[nodeId_0] = {
              ...node_0,
              payload: {
                ...(node_0.payload ?? {}),
                ...overrides
              }
            } as typeof node_0;
          }
        }
        updatedDefinition = {
          ...updatedDefinition,
          nodes: updatedNodes_0
        };
      }

      // Must run after prop resolution but before workflow creation.
      if (hasNotificationNodes(updatedDefinition)) {
        const notificationValue = propValues?.["$notification"];
        if (!notificationValue || typeof notificationValue !== "string") {
          throw new Error("Notification channel must be configured before creating this workflow.");
        }
        let parsed: Record<string, unknown>;
        try {
          parsed = JSON.parse(notificationValue) as Record<string, unknown>;
        } catch {
          throw new Error("Invalid notification channel configuration. Please reconfigure the notification channel.");
        }
        if (typeof parsed?.channel !== "string" || typeof parsed?.destination !== "string") {
          throw new Error("Invalid notification channel configuration. Please reconfigure the notification channel.");
        }
        const channelDef = NOTIFICATION_CHANNELS[parsed.channel];
        if (!channelDef) {
          throw new Error("Invalid notification channel configuration. Please reconfigure the notification channel.");
        }

        // extras must be a plain object of string values (channels like SMS
        // rely on fields such as `from`). Reject arrays/primitives/null so
        // a malformed $notification JSON surfaces as a user-facing error
        // instead of silently producing an empty `from`.
        if (parsed.extras != null) {
          if (typeof parsed.extras !== "object" || Array.isArray(parsed.extras) || !Object.values(parsed.extras).every(v => typeof v === "string")) {
            throw new Error("Invalid notification channel configuration. Please reconfigure the notification channel.");
          }
        }

        // Channel-specific: every field declared in extraFields must be
        // present and non-empty. The picker enforces this for UI-produced
        // configs, but $notification JSON can come from other sources too.
        if (channelDef.extraFields?.length) {
          const extras = parsed.extras as Record<string, string> | null | undefined ?? {};
          for (const field of channelDef.extraFields) {
            const raw = extras[field.key];
            if (typeof raw !== "string" || !raw.trim()) {
              throw new Error("Invalid notification channel configuration. Please reconfigure the notification channel.");
            }
          }
        }
        updatedDefinition = resolveNotificationNodes(updatedDefinition, parsed as unknown as NotificationConfig);
        if (hasNotificationNodes(updatedDefinition)) {
          throw new Error("Failed to resolve notification nodes. Please reconfigure the notification channel.");
        }
      }
      const newWorkflow = await createWorkflow({
        ...workflowRequest,
        definition: updatedDefinition
      });
      showToast.success(`Workflow "${newWorkflow.name}" created successfully!`);
      setShowConnectorModal(false);
      onSuccessRef.current?.();
      // Forward the onboarding flag through to the editor so the Publish
      // stepper is shown for users coming from the onboarding wizard.
      // Templates opened directly (without the param) navigate to the
      // editor without it, keeping the lighter advisory pill UI.
      const editorPath = fromOnboarding ? `/workflows/edit?id=${newWorkflow.id}&${FROM_ONBOARDING_QUERY_PARAM}=1` : `/workflows/edit?id=${newWorkflow.id}`;
      router.push(editorPath);
    } catch (error) {
      console.error("Failed to create workflow from template", error);
      showToast.error("Failed to create workflow. Please try again.");
    } finally {
      setIsCreatingWorkflow(false);
    }
  }, [templateToWorkflowRequest, applyConnectorMapping, createWorkflow, router, fromOnboarding]);

  /**
   * Connector modal's onSubmit — uses the template that was set when the modal opened.
   */
  const handleConnectorSubmit = useCallback(async (connectorMapping_1: Record<string, string>, propValues_0?: Record<string, unknown>, payloadOverrides_0?: Record<string, Record<string, unknown>>) => {
    if (!selectedTemplate) return;
    await createWorkflowFromTemplate(selectedTemplate, connectorMapping_1, propValues_0, payloadOverrides_0);
  }, [selectedTemplate, createWorkflowFromTemplate]);

  /**
   * Main entry point. Opens the connector modal when needed, otherwise creates directly.
   */
  const handleUseTemplate = useCallback(async (template_1: Template) => {
    setSelectedTemplate(template_1);
    const requiredConnectors = getRequiredTemplateConnectors(template_1);
    const hasProps = template_1.templateProps && template_1.templateProps.length > 0;
    const dbTables = getDBTables(template_1);
    if (requiredConnectors.length > 0 || hasProps || dbTables.length > 0) {
      setShowConnectorModal(true);
    } else {
      await createWorkflowFromTemplate(template_1, {});
    }
  }, [createWorkflowFromTemplate, getDBTables]);
  return {
    showConnectorModal,
    setShowConnectorModal,
    selectedTemplate,
    isCreatingWorkflow,
    handleUseTemplate,
    handleConnectorSubmit,
    requiredTables
  };
}
function buildPropDefaults(props: TemplateProp[] | undefined): Record<string, unknown> {
  if (!props) return {};
  const defaults: Record<string, unknown> = {};
  for (const prop of props) {
    if (prop.default !== undefined) defaults[prop.key] = prop.default;
  }
  return defaults;
}